import isFunction from "lodash/isFunction";
import isObject from "lodash/isPlainObject";
import merge from "lodash/merge";
import omitBy from "lodash/omitBy";
import isNil from "lodash/isNil";
import isEmpty from "lodash/isEmpty";
import moment from "moment-timezone";
import numbro from "numbro";
import { Icon, Message } from "semantic-ui-react";
import useAuthStore from "stores/useAuthStore";

import CONSTANTS, { SYSTEM_ROLES } from "constants/Constants";
import { isEnvelopeParam } from "components/NewAdvancedSearch/envelope";
import { TABLE_FORMAT_REGEX } from "constants/CustomFields";
import {
  applyTagFilters,
  getTagFilters,
} from "components/BaseTableV3/table-helpers";

// The coarse custom-table row-data ACL (wildcard form from acl-relationships.js).
const CUSTOM_TABLE_ROW_DATA_ACL_RE = /^\/custom_tables\/\*\/rows\*$/;
// A per-table row-data grant as stored in roles_permissions, e.g.
// "^/custom_tables/people/(rows|export)" (slug grammar mirrors the backend).
const CUSTOM_TABLE_ROW_DATA_GRANT_RE =
  /^\^\/custom_tables\/[a-z][a-z0-9_]*\/\(rows\|export\)$/;

function getUserTimeZone() {
  const userAuth = useAuthStore.getState().userAuth || {};
  return userAuth.timeZone || moment.tz.guess(true);
}

function formatCampaignStatus(status) {
  const statuses = {
    [CONSTANTS.CAMPAIGN_STATUS_TYPES.ACTIVE]: "Active",
    [CONSTANTS.CAMPAIGN_STATUS_TYPES.INACTIVE]: "Inactive",
    [CONSTANTS.CAMPAIGN_STATUS_TYPES.ARCHIVED]: "Archived",
  };
  return Object.keys(statuses).includes(String(status))
    ? statuses[status]
    : "ERROR: status not found";
}

function createDateTimeFormatter({ timeZone, format } = {}) {
  if (!timeZone) timeZone = getUserTimeZone();
  if (!format) format = "YYYY-MM-DD, hh:mm A z";
  //'08/28/2019, 12:00 AM'
  //https://momentjs.com/docs/#/displaying/format/

  function formatter(isoDateString) {
    if (!moment(isoDateString, moment.ISO_8601).isValid()) {
      return isoDateString;
    }
    const fmt = isFunction(format) ? format(isoDateString) : format;
    let formattedDateString = moment
      .utc(isoDateString)
      .tz(timeZone)
      .format(fmt);
    if (fmt.endsWith(" z")) {
      formattedDateString = formattedDateString.replace(/ [+-]\d+$/, " UTC$&");
    }
    return formattedDateString;
  }

  return formatter;
}

function convertToLocalDateTime(isoDateString, timeZone) {
  if (!timeZone) timeZone = getUserTimeZone();
  return moment.utc(isoDateString).tz(timeZone);
}

function toDateString(value) {
  if (!value) return null;
  if (typeof value === "string") {
    const parsed = moment.utc(value, "YYYY-MM-DD", true);
    return parsed.isValid() ? parsed.format("YYYY-MM-DD") : null;
  }
  return value.format("YYYY-MM-DD");
}

const formatDate = createDateTimeFormatter();

const stringToDateTime = (dateTimeStr, timeZone = "UTC") => {
  return moment
    .utc(dateTimeStr)
    .tz(timeZone || "UTC")
    .utcOffset(0, true);
};

const tableTimestamp = value => {
  if (value !== undefined && value !== null) {
    return moment().to(moment(convertToLocalDateTime(value)));
  }
  return null;
};

const keyIsUnique = ({ key, array }) => {
  return array.length === 0
    ? true
    : !array.some(item => item.key.toLowerCase() === key.toLowerCase());
};

const updateArrayItem = (array, key, newItem = null) => {
  const index = array.findIndex(item => item.key === key);
  if (index === -1) {
    // append item if not found
    return newItem !== null ? [...array, newItem] : [...array];
  }
  const itemsBefore = array.slice(0, index);
  const itemsAfter = array.slice(index + 1);
  return newItem !== null
    ? [...itemsBefore, newItem, ...itemsAfter]
    : [...itemsBefore, ...itemsAfter];
};

const updateCustomField = (custom, setCustom, key, value = null) => {
  const customArr = updateArrayItem(custom, key, {
    key,
    value,
  });
  setCustom(customArr);
};

const checkIsCustomFieldsReady = customFieldConfigs => {
  return customFieldConfigs
    .filter(cf => cf.required || (cf.field_type === "table" && !!cf.value))
    .every(cf => {
      // Boolean fields are always considered ready
      if (cf.field_type === "boolean") {
        return true;
      }

      // For table type fields, check if it matches the table format
      if (cf.field_type === "table" && cf.value) {
        return TABLE_FORMAT_REGEX.test(cf.value);
      }

      // For all other fields, check if they have a value
      return !!cf.value;
    });
};

const getCustomFieldsForSubmit = (customFieldConfigs, customFields) => {
  return customFields.reduce((obj, cf) => {
    const cfConfig = customFieldConfigs.find(
      cfc => cfc.label.toLowerCase() === cf.key.toLowerCase()
    );
    if (
      !cfConfig ||
      (!cfConfig.required &&
        ((typeof cf.value === "string" && !cf.value.trim()) ||
          (typeof cf.value === "object" &&
            Array.isArray(cf.value) &&
            !cf.value.some(i => i.trim())) ||
          (!cf.value && cf.value !== 0 && cf.value !== false)))
    ) {
      return obj;
    }
    switch (cfConfig.field_type) {
      case "date":
        obj[cf.key] =
          typeof cf.value === "object"
            ? cf.value.toISOString().split("T")[0]
            : cf.value;
        break;
      case "numeric":
        obj[cf.key] = Number(cf.value);
        break;
      case "select":
        obj[cf.key] =
          cfConfig.field_options.type === "single" && !Array.isArray(cf.value)
            ? [cf.value]
            : cf.value;
        if (
          !cf.value.length > 0 ||
          (cf.value.length === 1 && cf.value[0] === "")
        ) {
          delete obj[cf.key];
        }
        break;
      case "table":
        // Validate table format before submitting
        if (!TABLE_FORMAT_REGEX.test(cf.value)) {
          // If format is incorrect, don't submit this field
          console.warn(`Table format incorrect for field: ${cf.key}`);
        } else {
          obj[cf.key] = cf.value.trim();
        }
        break;
      default:
        obj[cf.key] = cf.value;
    }
    return obj;
  }, {});
};

const deleteArrayItem = (array, key) => {
  const index = array.findIndex(item => item.key == key);
  return [...array.slice(0, index), ...array.slice(index + 1)];
};

const extractCustom = info => {
  if (info !== undefined && info !== null) {
    return Object.keys(info)
      .map(key => {
        return { key: key, value: info[key] };
      })
      .sort((a, b) => {
        const keyA = a.key.toUpperCase(); // ignore upper and lowercase
        const keyB = b.key.toUpperCase(); // ignore upper and lowercase
        if (keyA < keyB) {
          return -1;
        }
        if (keyA > keyB) {
          return 1;
        }
        // names must be equal
        return 0;
      });
  } else {
    return [];
  }
};

const objectIsEmpty = obj => {
  return Object.keys(obj).length === 0 && obj.constructor === Object;
};

const objToArr = obj => {
  let arr = [];
  if (!!obj && !objectIsEmpty(obj)) {
    let keys = Object.keys(obj);
    for (const k of keys) {
      arr.push(obj[k]);
    }
  }
  return arr;
};

const compareCreatedAt = (a, b) => {
  if (a.created_at < b.created_at) {
    return 1;
  }
  if (a.created_at > b.created_at) {
    return -1;
  }
  return 0;
};

const LoadingMessage = () => (
  <Message icon>
    <Icon name="circle notched" loading />
    <Message.Content>
      <Message.Header>Just one second</Message.Header>
      We are fetching that content for you.
    </Message.Content>
  </Message>
);

const CustomLoader = ({ loading, style = {} }) => {
  return loading ? (
    <div className="-loading -active">
      <Message icon className="-loading-inner" style={style}>
        <Icon name="circle notched" loading />
        <Message.Content>
          <Message.Header>Just one second</Message.Header>
          We are fetching that content for you.
        </Message.Content>
      </Message>
    </div>
  ) : (
    <div />
  );
};

const capitalize = string => {
  if (typeof string !== "string") return null;
  return string.charAt(0).toUpperCase() + string.slice(1);
};

const sortByKey = (array, key, desc = false) => {
  return array.sort(function (a, b) {
    const [x, y] = [a[key], b[key]];
    const f = desc ? -1 : 1;
    return f * (x < y ? -1 : x > y ? 1 : 0);
  });
};

const getFormEvents = info => {
  if (
    info &&
    info.typeform_events &&
    info.typeform_events.some(r => r.event_type === "form_response")
  )
    return info.typeform_events;
  if (info && info.custom_form_events && info.custom_form_events.length > 0)
    return info.custom_form_events;
  return null;
};

const isUrl = str => {
  try {
    new URL(str);
    return true;
  } catch (e) {
    return false;
  }
};

const SAFE_URL_PROTOCOLS = new Set(["http:", "https:", "mailto:", "tel:"]);

// SECURITY: isUrl only checks that a string parses, so it accepts
// javascript: and data: URLs. Anything that becomes an href must go through
// this allowlist instead.
const isSafeUrl = str => {
  try {
    return SAFE_URL_PROTOCOLS.has(new URL(str).protocol);
  } catch {
    return false;
  }
};

const checkIsAuthorized = (
  permissions, // [{method: "get", path: "campaigns"}]
  {
    partialMatch = false,
    shouldReturnGrantedInfo = false,
    userAuth: user = {},
    permissionCategories = {},
  } = {}
) => {
  user = useAuthStore.getState().userAuth || user;
  permissionCategories =
    useAuthStore.getState().permissionCategories || permissionCategories;
  const userRole = user?.role || {};
  let userPermissions = userRole?.permissions || []; // set as const

  const isAdmin = userRole.id === SYSTEM_ROLES.ADMIN;

  let isAuthorized = false;
  let grantedInfo = [];

  if (isAdmin) {
    isAuthorized = true;
    grantedInfo = permissions.filter(Boolean).map(permission => ({
      ...permission,
      granted: true,
    }));
  } else {
    permissions = permissions.filter(Boolean).map(({ method, path }) => ({
      method,
      path,
      category: (Object.entries(permissionCategories).find(
        ([category, paths]) => !!paths.find(p => path.match(RegExp(p)))
      ) || [null])[0],
    }));

    const checkResults = permissions.map(({ method, path, category }) => {
      const userPermission = userPermissions.find(
        p =>
          p.is_allowed &&
          method === p.method &&
          (category === "__default__" ||
            `@${category}` === p.path ||
            path.replaceAll("*", 0).match(RegExp(p.path)) ||
            // Per-table grants store a concrete slug ("^/custom_tables/people/
            // (rows|export)"), so the wildcard `*`->`0` substitution can never
            // match them. Recognize the row-data grant shape directly so a
            // per-table grant satisfies the coarse row-data ACL.
            (CUSTOM_TABLE_ROW_DATA_ACL_RE.test(path) &&
              CUSTOM_TABLE_ROW_DATA_GRANT_RE.test(p.path)))
      );
      grantedInfo = [
        ...grantedInfo,
        { method, path, granted: !!userPermission },
      ];
      return userPermission;
    });
    isAuthorized = partialMatch
      ? checkResults.some(_ => _)
      : checkResults.every(_ => _);
  }

  if (shouldReturnGrantedInfo) {
    return { isAuthorized, grantedInfo };
  }
  return isAuthorized;
};

const mergeObjectValues = object => {
  const values = Object.values(object);
  if (values.length === 0 || Array.isArray(values[0])) {
    return values.reduce((obj, cur) => [...obj, ...cur], []);
  } else {
    return values.reduce((obj, cur) => ({ ...obj, ...cur }), {});
  }
};

const swapElements = (array, index1, index2) => {
  const res = [...array];
  [res[index1], res[index2]] = [res[index2], res[index1]];
  return res;
};

const normalizePercentage = (d0, d1) => {
  let value = d0 / d1;
  if (d1 === 0) {
    value = 0;
  }
  return value;
};

const getPercentage = (d0, d1, complement = false) => {
  let t = complement ? 1 : 0;
  return numbro(Math.abs(t - normalizePercentage(d0, d1))).format({
    output: "percent",
    mantissa: 1,
  });
};

const roundToTwoDecimals = value => {
  return typeof value === "number" &&
    !Number.isInteger(value) &&
    value.toString().split(".")[1].length > 2
    ? value.toFixed(2)
    : value;
};

const buildPrice = value => {
  const absValue = Math.abs(value);

  const formattedValue = absValue.toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

  if (value < 0) {
    return `-$${formattedValue}`;
  }
  return `$${formattedValue}`;
};

const getInvoicesRowAmount = invoice => {
  const amount = parseFloat(invoice.amount);
  if (isNaN(amount)) {
    return 0;
  }

  // A whole-term price already covers the full license, so only a per-year
  // price is multiplied by the term.
  const term =
    invoice.price_basis === "whole_term" || !invoice.term
      ? 1
      : parseInt(invoice.term);
  const quantity = invoice.quantity ? parseInt(invoice.quantity) : 1;

  let result = amount * term * quantity;

  if (invoice.is_discount) {
    return result * -1;
  }

  return result;
};

const getInvoicesTotalValue = invoices => {
  const overrideInvoice = invoices.filter(
    invoice => invoice.is_total_value_override === true
  );
  if (overrideInvoice.length > 0) {
    return getInvoicesRowAmount(overrideInvoice[0]);
  }

  return invoices.reduce((sum, invoice) => {
    sum += getInvoicesRowAmount(invoice);
    return sum;
  }, 0);
};

/**
 * Sets the property of all items in the [array] to a [value], except for the [exceptIndex].
 * @param {Array} array
 * @param {String} propName
 * @param {*} value
 * @param {Number} exceptIndex
 */
const setEveryItemPropertyAs = (array, propName, value, exceptIndex) => {
  for (let i = 0; i < array.length; i++) {
    if (exceptIndex !== undefined && i !== exceptIndex) {
      array[i][propName] = value;
    }
  }
};
const sanitizeCSSVarName = str =>
  str.replace(/[^a-zA-Z0-9_\u0080-\uFFFF-]/g, "-");

const getNextQueryParams = (queryParams, columns) => {
  let sortData = {};
  let tagsData = {};

  const sort_by = queryParams?.sort_by;
  const sort_dir = queryParams?.sort_dir;

  if (sort_by && sort_dir) {
    const column = columns.find(
      c => c.id === sort_by || c.accessorKey === sort_by
    );
    if (column) {
      sortData = {
        sort_by: column.sortKey ?? column.accessorKey,
        sort_dir: sort_dir,
      };
    }
  }

  // A clause array is already what the backend reads, and it is copied through
  // below; expanding it too would send the same predicate twice. Only a legacy
  // `1,2;-3` string still needs expanding into search:tags:<n>.
  if (queryParams?.tags && !isEnvelopeParam(queryParams.tags)) {
    const tagFilters = getTagFilters(queryParams.tags);
    const queryTagFilters = applyTagFilters(tagFilters);

    tagsData = {
      ...queryTagFilters,
    };
  }

  const withObject = Object.keys(queryParams).reduce((acc, key) => {
    if (isObject(queryParams[key])) {
      const objectFilter = queryParams[key];
      let objectFilterData = {};
      if (objectFilter.type) {
        objectFilterData[`${key}:type`] = objectFilter.type;
      }
      if (objectFilter.start) {
        objectFilterData[`${key}:start`] = objectFilter.start;
      }
      if (objectFilter.end) {
        objectFilterData[`${key}:end`] = objectFilter.end;
      }
      return { ...acc, ...objectFilterData };
    }
    return { ...acc, [key]: queryParams[key] };
  }, {});

  const finalQueryParams = { ...withObject, ...sortData, ...tagsData };

  return finalQueryParams;
};

const mergeSkipNullish = ({ target = {}, source = {} }) => {
  if (isEmpty(target)) {
    return source;
  }

  return merge({}, target, omitBy(source, isNil));
};

export {
  keyIsUnique,
  updateArrayItem,
  updateCustomField,
  checkIsCustomFieldsReady,
  getCustomFieldsForSubmit,
  deleteArrayItem,
  extractCustom,
  objectIsEmpty,
  objToArr,
  getUserTimeZone,
  createDateTimeFormatter,
  formatCampaignStatus,
  formatDate,
  tableTimestamp,
  stringToDateTime,
  convertToLocalDateTime,
  compareCreatedAt,
  LoadingMessage,
  CustomLoader,
  capitalize,
  sortByKey,
  getFormEvents,
  isUrl,
  isSafeUrl,
  checkIsAuthorized,
  mergeObjectValues,
  swapElements,
  getPercentage,
  roundToTwoDecimals,
  buildPrice,
  getInvoicesRowAmount,
  getInvoicesTotalValue,
  setEveryItemPropertyAs,
  getNextQueryParams,
  mergeSkipNullish,
  toDateString,
  sanitizeCSSVarName,
};
